// ═══════════════════════════════════════════════════════════════════════════
// PAGE EVOLUTION — construction PURE des données du graphique
// Extrait de page-evolution.ts (découpe god-function `renderEvolutionPage` 2026-07) :
// transforme les révisions filtrées en labels + datasets empilés + yMax. Aucun
// état de closure — niveaux et couleurs de sévérité (immuables) sont threadés.
// Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : t, fmtDate.

import type { RevisionWithCounts } from './page-evolution.js';

interface ChartBuildResult {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor: string;
    borderWidth: number;
    _level: number;
    _revs: RevisionWithCounts[];
  }>;
  yMax: number;
}

/** Construit labels / datasets empilés (un par niveau de sévérité) / borne yMax
 * arrondie au multiple de 5, à partir des révisions déjà filtrées. */
export function buildEvolutionChart(
  revs: RevisionWithCounts[],
  sevLevels: readonly number[],
  sevColors: Record<number, string>,
): ChartBuildResult {
  const labels = revs.map((r) =>
    r.is_closed ? fmtDate(r.date_end) : t('pageEvolution.labelInProgress', { name: r.name }),
  );

  const datasets = sevLevels.map((lv) => ({
    label: t('pageEvolution.severityShort', { lv }),
    data: revs.map((r) => (r.counts[lv] || []).length),
    backgroundColor: sevColors[lv] || '#cccccc',
    borderWidth: 0,
    _level: lv,
    _revs: revs,
  }));

  const totals = revs.map((r) => sevLevels.reduce((s, lv) => s + (r.counts[lv] || []).length, 0));
  const rawMax = Math.max(5, ...totals);
  const yMax = Math.ceil(rawMax / 5) * 5;

  return { labels, datasets, yMax };
}
